import React, { useEffect, useRef } from "react";
import moment from "moment";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  InputAdornment,
  FormControlLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
// import { DateTimePicker } from "@mui/x-date-pickers";
import { useDispatch, useSelector } from "react-redux";
import { Info } from "@mui/icons-material";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { period, periodLabels } from "utils/constants";
import { fetchBusiness } from "modules/Business/actions";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { getBalanceDisplay } from 'utils/balanceColor';
import OfferSaveActions from '../OfferSaveActions';
import WizardBackButton from '../WizardBackButton';
import { prepareBudgetValues } from './utils';
import { formatBudgetEstimate, useBudgetEstimate } from '../../budgetEstimate';

import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { browserTimeZone } from 'utils/time';
import { isMissingLocalTime, scheduleDisplay } from '../../schedule';

const optionalNumber = () => yup.number().transform((value, originalValue) => (
  originalValue === '' || originalValue == null ? undefined : value
));

const schema = yup.object().shape({
  maxAmount: optionalNumber()
    .typeError("Enter a maximum spend of at least $1.")
    .min(1, "Enter a maximum spend of at least $1.")
    .required("Enter a maximum spend."),
  amountPeriod: optionalNumber().required("Select how often the spend limit resets."),
  unlimitedClaims: yup.boolean(),
  maxClick: optionalNumber().when('unlimitedClaims', {
    is: true,
    then: (field) => field.notRequired(),
    otherwise: (field) => field
      .typeError("Enter a maximum number of claims of at least 1.")
      .min(1, "Enter a maximum number of claims of at least 1.")
      .required("Enter a maximum number of claims."),
  }),
  clickPeriod: optionalNumber().when('unlimitedClaims', {
    is: true,
    then: (field) => field.notRequired(),
    otherwise: (field) => field.required("Select how often the claim limit resets."),
  }),
  startDate: yup.string().required("Select a start date."),
  endDate: yup.string()
    .required("Select an end date.")
    .test('after-start-date', 'End date and time must be after the start date and time.', function isAfterStart(value) {
      const { startDate } = this.parent;
      return !value || !startDate || moment(value).isAfter(moment(startDate));
    }),
});

const OfferBudget = ({
  back,
  onComplete,
  values,
  onSave,
  onUpdate,
  onValuesChange,
  saveButtonLabel,
}) => {
  const dispatch = useDispatch();
  const { businessBudget } = useSelector(
    (state) => state.business || {}
  );

  const business = useSelector((state) => state.business || {});

  useEffect(() => {
    if (business?.businessId) {
      dispatch(fetchBusiness(business.businessId, true));
    }
  }, [dispatch, business?.businessId]);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isDirty, touchedFields },
    watch,
    getValues,
    setValue,
    trigger,
    setError,
    clearErrors,
  } = useForm({
    defaultValues: values?.budget || {
      amountPeriod: period.daily,
      clickPeriod: period.daily,
      maxAmount: 5,
      maxClick: 5,
      unlimitedClaims: false,
      startDate: moment().toISOString(),
      timeZone: browserTimeZone(),
    },
    resolver: yupResolver(schema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  const rawTimes = useRef({});
  const acceptTime = (name, value, validate = false) => {
    if (isMissingLocalTime(rawTimes.current[name])) {
      setValue(name, undefined, { shouldDirty: true });
      setError(name, { type: 'validate', message: 'This time does not exist because of daylight saving time. Choose another time.' });
      return;
    }
    clearErrors(name);
    const nextValue = value?.isValid() ? value.toISOString() : undefined;
    const currentValue = getValues(name);
    if (nextValue && currentValue && moment(nextValue).isSame(moment(currentValue))) {
      if (validate) void trigger(name === 'startDate' ? ['startDate', 'endDate'] : 'endDate');
      return;
    }
    setValue('timeZone', browserTimeZone(), { shouldDirty: true });
    setValue(name, nextValue, {
      shouldDirty: true, shouldTouch: validate, shouldValidate: validate,
    });
    if (validate && name === 'startDate') void trigger('endDate');
  };

  const currentValues = watch();
  const unlimitedClaims = Boolean(currentValues.unlimitedClaims);
  const estimate = useBudgetEstimate(onUpdate ? null : business.businessId, { ...values, budget: currentValues });

  useEffect(() => {
    const touchedNumbers = ['maxAmount', 'maxClick']
      .filter((fieldName) => touchedFields[fieldName]);
    if (touchedNumbers.length > 0) {
      void trigger(touchedNumbers);
    }
  }, [
    currentValues.maxAmount,
    currentValues.maxClick,
    touchedFields,
    trigger,
  ]);

  useEffect(() => {
    onValuesChange?.('budget', prepareBudgetValues(currentValues), isDirty);
  }, [currentValues, isDirty, onValuesChange]);

  const minimumStartDate = dayjs().startOf('day');
  const selectedStartDate = dayjs(currentValues.startDate);
  const minimumEndDate = selectedStartDate.isValid()
    && selectedStartDate.isAfter(minimumStartDate, 'day')
    ? selectedStartDate.startOf('day')
    : minimumStartDate;

  useEffect(() => {
    if (businessBudget?.businessId) {
      dispatch(fetchBusiness(businessBudget.businessId, true));
    }
  }, [dispatch, businessBudget?.businessId]);

  const handleFormSubmit = (data) => {
    onComplete(prepareBudgetValues(data));
  };

  return (
    <>
      <Box mb={2}>
        <Typography variant="h6">Set a total budget for your offers</Typography>
        <Typography variant="body2" component="p" gutterBottom>
          You can allocate and adjust portions of this amount to each offer you
          create
          <Tooltip title={
            <>
              <Typography variant="h6">How your budget works</Typography>
              <Typography variant="body2">What you pay for</Typography>
              <Typography variant="caption" paragraph>Pay only when people claim your offer.</Typography>
              <Typography variant="body2">Set your max spend</Typography>
              <Typography variant="caption" paragraph>Costs can vary per claim, but you won’t spend more than your max spend for the selected amount of time.</Typography>
              <Typography variant="body2">Set your max claims</Typography>
              <Typography variant="caption" paragraph>Set a claim limit for a selected duration, or allow unlimited claims. Your offer will still pause when its spend limit is reached.</Typography>
            </>
          }>
            <IconButton size="small">
              <Info />
            </IconButton>
          </Tooltip>
        </Typography>
      
      </Box>
      <Grid container spacing={2}>
        <Grid item container spacing={2} xs={12} md={8}>
          {/* Max Spend */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth variant="outlined" error={!!errors.maxAmount}>
              <TextField
                label="Max spend"
                {...register("maxAmount")}
                variant="outlined"
                margin="dense"
                type="number"
                error={!!errors.maxAmount}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
              {errors.maxAmount && (
                <FormHelperText error>{errors.maxAmount.message}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          {/* Budget Period */}
          <Grid item xs={12} sm={6}>
  <FormControl variant="outlined" fullWidth margin="dense" error={!!errors.amountPeriod}>
    <InputLabel id="budget-period">Period</InputLabel>
    <Controller
      name="amountPeriod"
      control={control}
      render={({ field }) => (
        <Select
          {...field}
          label="Period"
          labelId="budget-period"
          onChange={(event) => setValue('amountPeriod', event.target.value, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          })}
        >
          {Object.entries(period).map(([key, value]) => (
            <MenuItem key={value} value={value}>
              {periodLabels[value]} {/* Corrected key access */}
            </MenuItem>
          ))}
        </Select>
      )}
    />
    {errors.amountPeriod && (
      <FormHelperText error>{errors.amountPeriod.message}</FormHelperText>
    )}
  </FormControl>
          </Grid>
          {estimate && (
            <Grid item xs={12}>
              <Typography variant="body2" role="status">{formatBudgetEstimate(estimate)}</Typography>
            </Grid>
          )}
          <Grid item xs={12}>
            <Controller
              name="unlimitedClaims"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={(
                    <Switch
                      checked={Boolean(field.value)}
                      onChange={(_, checked) => {
                        setValue('unlimitedClaims', checked, {
                          shouldDirty: true,
                          shouldTouch: true,
                          shouldValidate: true,
                        });
                        void trigger(['maxClick', 'clickPeriod']);
                      }}
                    />
                  )}
                  label="Unlimited claims"
                />
              )}
            />
            <Typography variant="caption" color="textSecondary" display="block">
              Allow claims until the offer reaches its spend or time limit.
            </Typography>
          </Grid>
          {/* Max Clicks */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth variant="outlined" error={!unlimitedClaims && !!errors.maxClick}>
              <TextField
                label="Max claims"
                {...register("maxClick")}
                variant="outlined"
                margin="dense"
                type="number"
                disabled={unlimitedClaims}
                error={!unlimitedClaims && !!errors.maxClick}
              />
              {!unlimitedClaims && errors.maxClick && (
                <FormHelperText error>{errors.maxClick.message}</FormHelperText>
              )}
            </FormControl>
          </Grid>

   
          <Grid item xs={12} sm={6}>
  <FormControl
    variant="outlined"
    fullWidth
    style={{ margin: "8px 0" }}
    error={!unlimitedClaims && !!errors.clickPeriod}
  >
    <InputLabel id="click-period">Period</InputLabel>
    <Controller
      name="clickPeriod"
      control={control}
      render={({ field }) => (
        <Select
          {...field}
          value={field.value ?? ''}
          fullWidth
          labelId="click-period"
          margin="dense"
          variant="outlined"
          label="Period"
          disabled={unlimitedClaims}
          onChange={(event) => setValue('clickPeriod', event.target.value, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          })}
        >
          {Object.entries(period).map(([key, value]) => (
            <MenuItem key={value} value={value}>
              {periodLabels[value]} {/* Corrected: Using periodLabels[value] instead of key */}
            </MenuItem>
          ))}
        </Select>
      )}
    />
    {!unlimitedClaims && errors.clickPeriod && (
      <FormHelperText error>{errors.clickPeriod.message}</FormHelperText>
    )}
  </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Box mt={2}>
              <Typography variant="h6" component="h2">
                Set a time limit for your offers
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Times are shown in your browser time zone ({browserTimeZone()}).
              </Typography>
            </Box>
          </Grid>

          {/* Start Date */}

<Grid item xs={6}>
  <FormControl fullWidth onChangeCapture={(event) => { rawTimes.current.startDate = event.target.value; }}>
    <Controller
      name="startDate"
      control={control}
      render={({ field: { onBlur, value } }) => (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DateTimePicker
            ampm={true}
            format="MM/DD/YYYY hh:mm A"
            onOpen={() => { rawTimes.current = {}; }}
            value={value ? dayjs(value) : null}
            minDate={minimumStartDate}
            onChange={(val) => acceptTime('startDate', val)}
            onAccept={(val) => acceptTime('startDate', val, true)}
            //views={['year', 'day', 'hours', 'minutes']}
            //format={dateTimeFormat} // e.g., 'YYYY-MM-DD HH:mm'
            slotProps={{
              textField: {
                label: 'Start date',
                margin: 'dense',
                fullWidth: true,
                error: !!errors.startDate,
                helperText: errors.startDate?.message || scheduleDisplay(currentValues.startDate),
                onBlur: () => {
                  onBlur();
                  void trigger(['startDate', 'endDate']);
                },
              }
            }}
          />
        </LocalizationProvider>
      )}
    />
  </FormControl>
</Grid>

          {/* End Date */}
          <Grid item xs={6}>
  <FormControl fullWidth onChangeCapture={(event) => { rawTimes.current.endDate = event.target.value; }}>
    <Controller
      name="endDate"
      control={control}
      render={({ field: { onBlur, value } }) => (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DateTimePicker
            ampm={true}
            format="MM/DD/YYYY hh:mm A"
            onOpen={() => { rawTimes.current = {}; }}
            value={value ? dayjs(value) : null}
            minDate={minimumEndDate}
            onChange={(val) => acceptTime('endDate', val)}
            onAccept={(val) => acceptTime('endDate', val, true)}
            slotProps={{
              textField: {
                label: 'End date',
                margin: 'dense',
                fullWidth: true,
                error: !!errors.endDate,
                helperText: errors.endDate?.message || scheduleDisplay(currentValues.endDate),
                onBlur: () => {
                  onBlur();
                  void trigger('endDate');
                },
              }
            }}
          />
        </LocalizationProvider>
      )}
    />
  </FormControl>
</Grid>
        </Grid>
      </Grid>

      <Box mt={6} mb={1} display="flex" justifyContent="space-between" flexWrap="wrap" gap={2}>
        <OfferSaveActions
          onUpdate={onUpdate
            ? handleSubmit((data) => onUpdate({ budget: prepareBudgetValues(data) }))
            : undefined}
          onSave={() => onSave({ budget: prepareBudgetValues(getValues()) })}
          saveButtonLabel={saveButtonLabel}
        />

        <Box display="flex" alignItems="center">
           {/* Display Account Balance Message */}
           {(() => {
    try {
      const balanceDisplay = getBalanceDisplay(
        business.accountBalance,
        'Account balance:',
        business
      );
      return (
        <Typography variant="body2" color={balanceDisplay.color}>
          {balanceDisplay.text}
        </Typography>
      );
    } catch (error) {
      // Handle any potential errors (e.g., if accountBalance is not in the expected format)
   
      return (
        <Typography variant="body2" color="error">
          Unable to display account balance. Please check your account settings.
        </Typography>
      );
    }
  })()}
          <WizardBackButton onClick={back} />
          <Button
            color="primary"
            onClick={handleSubmit(handleFormSubmit)}
            variant="contained"
            type="submit"
            style={{ marginRight: '16px' }}
          >
            Next
          </Button>
          
        </Box>
      </Box>
    </>
  );
};

export default OfferBudget;
